import { api } from './api';

export interface ContactAssignment {
  link_id: string;
  site_id: string;
  site_name: string | null;
  site_role: string | null;
  notify_email: boolean;
  notify_sms: boolean;
  is_primary: boolean;
  is_active: boolean;
}

export interface ContactDetail {
  id: string; // global user id
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  company: string | null;
  assignment_count: number;
  assignments: ContactAssignment[];
}

export interface ContactIdentityUpdate {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  company?: string;
}

export interface SiteAssignmentUpdate {
  site_role?: string;
  notify_email?: boolean;
  notify_sms?: boolean;
  is_primary?: boolean;
}

/**
 * Global contact management — one record per person across all their sites.
 * Counterpart to sitesService.getContacts (which is site-scoped).
 */
export interface ContactListItem {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  company: string | null;
  assignment_count: number;
}

export const contactsService = {
  /** People linked to sites; with `organizationId`, that client's own people (the site-contact pick list). */
  async list(params: {
    search?: string;
    organizationId?: string;
    limit?: number;
  }): Promise<ContactListItem[]> {
    const res = await api.get<ContactListItem[]>('/contacts', {
      params: {
        search: params.search || undefined,
        organization_id: params.organizationId || undefined,
        limit: params.limit ?? 200,
      },
    });
    return res.data;
  },
  async get(userId: string): Promise<ContactDetail> {
    const res = await api.get<ContactDetail>(`/contacts/${userId}`);
    return res.data;
  },

  async updateIdentity(
    userId: string,
    data: ContactIdentityUpdate
  ): Promise<ContactDetail> {
    const res = await api.patch<ContactDetail>(`/contacts/${userId}`, data);
    return res.data;
  },

  async addToSite(
    userId: string,
    data: {
      site_id: string;
      site_role?: string;
      notify_email?: boolean;
      is_primary?: boolean;
    }
  ): Promise<ContactAssignment> {
    const res = await api.post<ContactAssignment>(
      `/contacts/${userId}/sites`,
      data
    );
    return res.data;
  },

  async updateSite(
    userId: string,
    siteId: string,
    data: SiteAssignmentUpdate
  ): Promise<ContactAssignment> {
    const res = await api.patch<ContactAssignment>(
      `/contacts/${userId}/sites/${siteId}`,
      data
    );
    return res.data;
  },

  async removeFromSite(userId: string, siteId: string): Promise<void> {
    await api.delete(`/contacts/${userId}/sites/${siteId}`);
  },
};
